(() => {
  'use strict';
  const KEY = 'bexco.vehicle.v1';
  const GENERATIONS = ['swb', 'lwb', 'g', '964', '993'];
  const BUILDS = ['original', '911r', 'rs', 'st', 'rsr', 'iroc', 'turbo', 'backdate', 'widebody', 'race'];
  const YEARS = {swb: [1965, 1968], lwb: [1969, 1973], g: [1974, 1989], '964': [1989, 1994], '993': [1994, 1998]};
  const requests = new Map();
  const safeText = (value, length = 600) => typeof value === 'string' ? value.replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, length) : '';
  const safeHandle = value => typeof value === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(value) ? value : '';
  const safeVariant = value => /^(?:[1-9]\d{0,19})$/.test(String(value || '')) ? String(value) : '';
  const itemKey = item => item.handle + ':' + (item.variantId || 'idea');
  const productLink = (handle, variantId, root = '/') => {
    const safe = safeHandle(handle), variant = safeVariant(variantId);
    const prefix = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?$/i.test(root) ? root : '/';
    return safe ? prefix + 'products/' + safe + (variant ? '?variant=' + variant : '') : '';
  };
  const itemFromLink = (href, title, optionLabel, origin) => {
    try {
      const url = new URL(href, origin);
      if (url.origin !== origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[a-z0-9][a-z0-9-]*\/?$/i.test(url.pathname)) return null;
      return {handle: url.pathname.replace(/\/$/, '').split('/').pop(), title, variantId: url.searchParams.get('variant'), options: optionLabel ? [optionLabel] : []};
    } catch (_) { return null; }
  };
  const cleanProfile = value => ({version: 1, generation: GENERATIONS.includes(value?.generation) ? value.generation : '', year: value?.year == null ? '' : safeText(String(value.year), 20), build: BUILDS.includes(value?.build) ? value.build : ''});
  const profileError = profile => {
    if (profile.year && (!/^\d{4}$/.test(profile.year) || Number(profile.year) < 1965 || Number(profile.year) > 1998)) return 'invalid_year';
    const range = YEARS[profile.generation];
    return range && profile.year && (Number(profile.year) < range[0] || Number(profile.year) > range[1]) ? 'year_conflict' : '';
  };
  const selectApplications = (record, profile) => {
    if (profileError(profile)) return [];
    const year = Number(profile.year) || null;
    return (Array.isArray(record?.applications) ? record.applications : []).filter(app => {
      if (!app || !Array.isArray(app.generations) || !Array.isArray(app.conditions)) return false;
      if (profile.generation && app.generations.length && !app.generations.includes(profile.generation)) return false;
      if (year && (Number.isInteger(app.yearMin) && year < app.yearMin || Number.isInteger(app.yearMax) && year > app.yearMax)) return false;
      return true;
    }).sort((a, b) => Number(Array.isArray(b.builds) && b.builds.includes(profile.build)) - Number(Array.isArray(a.builds) && a.builds.includes(profile.build))).slice(0, 8);
  };
  const cleanItems = value => {
    const seen = new Set();
    return (Array.isArray(value) ? value : []).slice(0, 20).flatMap(item => {
      const handle = safeHandle(item?.handle);
      const variantId = safeVariant(item?.variantId), key = itemKey({handle, variantId});
      if (!handle || seen.has(key)) return [];
      seen.add(key);
      const options = variantId && Array.isArray(item.options) ? item.options.slice(0, 3).map(value => safeText(value, 300)).filter(value => value && value !== 'Default Title') : [];
      return [{handle, title: safeText(item.title, 220) || handle, variantId, options}];
    });
  };
  const assetUrl = (value, origin) => {
    try {
      const url = new URL(value, origin);
      if (url.protocol !== 'https:' || url.username || url.password || url.port) return '';
      if (url.origin !== origin && url.hostname !== 'cdn.shopify.com') return '';
      return url.href;
    } catch (_) { return ''; }
  };
  const exportProfileLines = (rawProfile, labels) => {
    const profile = cleanProfile(rawProfile);
    if (profileError(profile)) return [];
    return [['generation', profile.generation && labels?.[profile.generation]], ['year', profile.year], ['build', profile.build && labels?.[profile.build]]].flatMap(([field, value]) => {
      const template = safeText(labels?.['export_' + field], 800);
      const text = safeText(value, 220);
      return template && text ? [template.replace('__VALUE__', () => text)] : [];
    });
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = {cleanProfile, profileError, selectApplications, cleanItems, itemKey, productLink, itemFromLink, assetUrl, exportProfileLines, KEY};
  if (typeof document === 'undefined') return;
  function loadRecords(url) {
    if (!requests.has(url)) requests.set(url, (async () => {
      if (!url) throw new Error('Missing fitment asset');
      const abort = new AbortController();
      const timer = setTimeout(() => abort.abort(), 12000);
      try {
        const response = await fetch(url, {method: 'GET', credentials: 'omit', cache: 'no-cache', redirect: 'error', signal: abort.signal, headers: {Accept: 'application/json'}});
        if (!response.ok) throw new Error('Fitment asset unavailable (' + response.status + ')');
        const raw = await response.text();
        if (raw.length > 2000000) throw new Error('Fitment asset too large');
        const data = JSON.parse(raw);
        if (!Array.isArray(data) || data.length > 500) throw new Error('Invalid fitment data');
        return new Map(data.filter(record => record?.schemaVersion === 1 && record.publishable === true && safeHandle(record.handle)).map(record => [record.handle, record]));
      } finally { clearTimeout(timer); }
    })());
    return requests.get(url);
  }
  function init() {
    const dialog = document.getElementById('BexcoBuildList');
    const panel = dialog?.querySelector('[data-bexco-fitment-planner]');
    if (!panel || panel.dataset.fitmentReady) return;
    let labels;
    try { labels = JSON.parse(panel.dataset.fitmentI18n || '{}'); } catch (_) { return; }
    if (!labels || typeof labels !== 'object' || !labels.review) return;
    panel.dataset.fitmentReady = 'true';
    const t = key => safeText(labels[key], 800);
    const controls = panel.querySelector('[data-fitment-controls]');
    const resultBox = panel.querySelector('[data-fitment-results]');
    const status = panel.querySelector('[data-fitment-status]');
    const privacy = panel.querySelector('[data-fitment-privacy]');
    const retry = panel.querySelector('[data-fitment-retry]');
    const fields = [...panel.querySelectorAll('[data-fitment-field]')];
    const root = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?$/i.test(panel.dataset.fitmentRoot || '') ? panel.dataset.fitmentRoot : '/';
    const url = assetUrl(panel.dataset.fitmentUrl, location.origin);
    let profile = cleanProfile(null), items = [], records = null, persistent = true, loading = false, failed = false;
    const node = (tag, value, className = '') => {
      const element = document.createElement(tag);
      if (value) element.textContent = value;
      if (className) element.className = className;
      return element;
    };
    const link = (handle, title, variantId = '') => {
      const element = node('a', title);
      element.href = productLink(handle, variantId, root);
      return element;
    };
    const syncFields = () => fields.forEach(field => { field.value = profile[field.dataset.fitmentField] || ''; });
    const syncPrivacy = () => { privacy.textContent = t(persistent ? 'privacy' : 'storage_unavailable'); };
    const readProfile = () => {
      try {
        const raw = localStorage.getItem(KEY);
        profile = raw && raw.length < 2000 ? cleanProfile(JSON.parse(raw)) : cleanProfile(null);
        persistent = true;
      } catch (_) { persistent = false; }
      syncFields(); syncPrivacy();
    };
    const saveProfile = () => {
      try { localStorage.setItem(KEY, JSON.stringify(profile)); persistent = true; }
      catch (_) { persistent = false; }
      syncPrivacy();
    };
    const currentDomItems = () => cleanItems([...dialog.querySelectorAll('[data-bexco-build-items] .bexco-build__title')].map(element => itemFromLink(
      element.href, element.textContent, element.closest?.('[data-line-key]')?.querySelector('[data-line-options]')?.textContent, location.origin
    )).filter(Boolean));
    const appendList = (owner, values) => {
      const list = node('ul');
      values.map(value => safeText(value)).filter(Boolean).slice(0, 20).forEach(value => list.append(node('li', value)));
      if (list.childElementCount) owner.append(list);
    };
    const render = () => {
      if (!panel.isConnected || !dialog.open) return;
      const error = profileError(profile);
      if (retry) { retry.hidden = !failed || loading || !items.length || !!error; retry.disabled = loading; }
      fields.find(field => field.dataset.fitmentField === 'year')?.setAttribute('aria-invalid', String(!!error));
      fields.find(field => field.dataset.fitmentField === 'generation')?.setAttribute('aria-invalid', String(error === 'year_conflict'));
      const openItems = new Set([...resultBox.querySelectorAll('details[open]')].map(element => element.dataset.fitmentKey));
      resultBox.replaceChildren();
      if (error) { status.textContent = t(error); return; }
      if (!items.length) { status.textContent = t('empty'); return; }
      if (loading) { status.textContent = t('loading'); return; }
      if (failed || !records) { status.textContent = t('unavailable'); return; }
      status.textContent = t(profile.generation ? 'updated' : 'choose');
      resultBox.append(node('p', t('scope'), 'bexco-fitment-planner__small'));
      const selected = new Set(items.map(item => item.handle));
      for (const item of items) {
        const record = records.get(item.handle);
        const part = node('details', '', 'bexco-fitment-planner__part');
        part.dataset.fitmentHandle = item.handle;
        part.dataset.fitmentKey = itemKey(item);
        part.open = openItems.has(itemKey(item));
        part.append(node('summary', item.title + (item.options.length ? ' · ' + item.options.join(' / ') : '')));
        part.append(node('p', t('review')));
        if (record?.summary) part.append(node('p', safeText(record.summary)));
        if (record?.configurationRequired && record.configurationVerified !== true) part.append(node('p', t('configuration_review')));
        const applications = selectApplications(record, profile);
        if (!applications.length) part.append(node('p', t('no_application')));
        for (const application of applications) {
          const route = node('div', '', 'bexco-fitment-planner__route');
          const generationLabel = application.generations.filter(value => GENERATIONS.includes(value)).map(t).join(' / ');
          route.append(node('h4', generationLabel || t('component_only')));
          if (Number.isInteger(application.yearMin) && Number.isInteger(application.yearMax)) route.append(node('p', t('year_range').replace('__FIRST__', String(application.yearMin)).replace('__LAST__', String(application.yearMax)), 'bexco-fitment-planner__small'));
          const installation = ['normal_fitting', 'supporting_parts', 'fabrication', 'unverified'].includes(application.installation) ? application.installation : 'unverified';
          route.append(node('p', t(installation)));
          if (application.conditions.length) {
            route.append(node('p', t('check_conditions')));
            appendList(route, application.conditions.map(condition => condition?.label));
            route.append(node('p', t('conditions_unknown'), 'bexco-fitment-planner__small'));
          }
          appendList(route, Array.isArray(application.notes) ? application.notes : []);
          const companions = (Array.isArray(application.requiredParts) ? application.requiredParts : []).filter(companion => safeHandle(companion?.handle));
          if (companions.length) {
            route.append(node('h4', t('companions')));
            const list = node('ul');
            for (const companion of companions.slice(0, 10)) {
              const row = node('li');
              row.append(link(companion.handle, safeText(companion.title, 220) || companion.handle));
              if (companion.reason) row.append(node('p', safeText(companion.reason)));
              row.append(node('p', t(selected.has(companion.handle) ? 'in_list' : 'not_in_list'), 'bexco-fitment-planner__small'));
              list.append(row);
            }
            route.append(list);
          }
          part.append(route);
        }
        if (Array.isArray(record?.exclusions) && record.exclusions.length) {
          part.append(node('h4', t('exclusions')));
          appendList(part, record.exclusions);
        }
        part.append(link(item.handle, t('product_link'), item.variantId));
        resultBox.append(part);
      }
    };
    const activate = () => {
      if (!dialog.open || !panel.isConnected) return;
      items = currentDomItems();
      if (!items.length || records || loading || failed) { render(); return; }
      loading = true; render();
      loadRecords(url).then(value => { records = value; }).catch(() => { failed = true; }).finally(() => { loading = false; render(); });
    };
    retry?.addEventListener('click', () => {
      if (!failed || loading || records) return;
      requests.delete(url);
      failed = false;
      activate();
    });
    panel.addEventListener('change', event => {
      if (!event.target.matches('[data-fitment-field]')) return;
      profile = cleanProfile(Object.fromEntries(fields.map(field => [field.dataset.fitmentField, field.value])));
      saveProfile(); render();
    });
    panel.querySelector('[data-fitment-clear]').addEventListener('click', () => {
      profile = cleanProfile(null);
      try { localStorage.removeItem(KEY); persistent = true; } catch (_) { persistent = false; }
      syncFields(); syncPrivacy(); render(); status.textContent = t('clear_done');
      fields[0]?.focus();
    });
    const closeForChat = event => {
      if (!(event.target instanceof Element) || !event.target.closest('[data-bexco-chat-open]')) return;
      if (event.type === 'keydown' && event.key !== ' ') return;
      dialog.querySelector('[data-bexco-build-close]')?.click();
    };
    panel.addEventListener('click', closeForChat);
    panel.addEventListener('keydown', closeForChat);
    document.addEventListener('bexco:build-changed', event => {
      if (!panel.isConnected) return;
      items = cleanItems(event.detail?.items);
      if (dialog.open && items.length && !records && !loading && !failed) activate();
      else render();
    });
    document.addEventListener('bexco:build-export', event => {
      if (!panel.isConnected || !Array.isArray(event.detail?.lines)) return;
      const current = cleanProfile(Object.fromEntries(fields.map(field => [field.dataset.fitmentField, field.value])));
      event.detail.lines.push(...exportProfileLines(current, labels));
    });
    window.addEventListener('storage', event => {
      if (!panel.isConnected || event.key !== KEY && event.key !== null) return;
      readProfile(); render();
    });
    new MutationObserver(activate).observe(dialog, {attributes: true, attributeFilter: ['open']});
    controls.hidden = false;
    readProfile(); activate();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once: true});
  else init();
  document.addEventListener('shopify:section:load', init);
})();
